import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { track } from "@/lib/api";
import {
  HandsPraying,
  ArrowLeft,
  ArrowRight,
  Sparkle,
  Wind,
  Compass,
  Heart,
  Sun,
  Microphone,
  StopCircle,
  PaperPlaneTilt,
  UsersThree,
  Eye,
  ShieldCheck,
  X,
} from "@phosphor-icons/react";

const SHORTCUTS = [
  { id: "calm",      label: "Calm my mind",       prefill: "I need to calm my mind. ",                     icon: Wind },
  { id: "direction", label: "Find direction",     prefill: "I'm seeking direction about ",                 icon: Compass },
  { id: "closer",    label: "Feel closer to God", prefill: "I want to feel closer to God. ",               icon: Heart },
  { id: "release",   label: "Release stress",     prefill: "I'm carrying a lot of stress around ",         icon: Sun },
];

const PROMPTS = [
  "What's on your heart right now?",
  "Tell Voice what you're carrying today.",
  "Bring what you need into prayer.",
];

export default function PersonalPrayerStartPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [intent, setIntent] = useState("");
  const [shortcut, setShortcut] = useState(params.get("shortcut") || null);
  const [generating, setGenerating] = useState(false);
  const [recording, setRecording] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const promptIdx = useRef(Math.floor(Math.random() * PROMPTS.length));

  // Pray-for-me-too share opt-in (default OFF — user must intentionally enable).
  const [shareOptIn, setShareOptIn] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Apply shortcut prefill on first mount.
  useEffect(() => {
    if (shortcut) {
      const sc = SHORTCUTS.find((s) => s.id === shortcut);
      if (sc) setIntent((cur) => cur || sc.prefill);
    }
  }, [shortcut]);

  const pickShortcut = (id) => {
    setShortcut(id);
    const sc = SHORTCUTS.find((s) => s.id === id);
    if (sc) setIntent((cur) => (cur ? cur : sc.prefill));
    track("personal_prayer_shortcut_click", { id });
  };

  const generate = async () => {
    const text = intent.trim();
    if (text.length < 4) { toast.error("Share a little more about what's on your heart."); return; }
    setGenerating(true);
    track("personal_prayer_generate_clicked", { length: text.length, shortcut, share_opt_in: shareOptIn });
    try {
      const r = await api.post("/prayer/personal/generate", { intent: text, shortcut });
      // Save immediately so we have a stable URL to navigate to + return later.
      const saved = await api.post("/prayer/personal", {
        intent: text, shortcut, journey: r.data.journey,
      });
      track("personal_prayer_generated", { id: saved.data.id, steps: saved.data.steps?.length });

      // If the user opted in to share, fire the share now using the previewed
      // (and possibly user-confirmed) summary. We require the preview step —
      // shareOptIn alone does NOT auto-share; previewData must exist.
      if (shareOptIn && previewData?.summary) {
        try {
          await api.post(`/prayer/personal/${saved.data.id}/share-to-circle`, {
            summary: previewData.summary,
            topic: previewData.topic,
            redactions: previewData.redactions || [],
          });
          track("personal_prayer_shared_to_circle", { id: saved.data.id, topic: previewData.topic });
          toast.success("Your prayer was shared anonymously. Others are praying with you.");
        } catch (e) {
          // Don't fail the whole flow — just notify.
          toast.error(e?.response?.data?.detail || "Couldn't share to a circle, but your journey is ready.");
        }
      }
      navigate(`/guided-prayer/personal/${saved.data.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't craft your prayer just now. Please try again.";
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  const loadPreview = async () => {
    const text = intent.trim();
    if (text.length < 4) { toast.error("Share a little more first."); return; }
    setPreviewLoading(true);
    track("personal_prayer_share_preview_viewed", {});
    try {
      const r = await api.post("/prayer/personal/anonymize-preview", { intent: text });
      setPreviewData(r.data);
      setShowPreview(true);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't generate a safe preview right now.";
      toast.error(detail);
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmPreview = () => {
    setShareOptIn(true);
    setShowPreview(false);
    track("personal_prayer_share_opt_in", { topic: previewData?.topic });
    toast.success("You'll be prayed for.");
  };

  const cancelPreview = () => {
    setShareOptIn(false);
    setShowPreview(false);
    setPreviewData(null);
  };

  // Voice input — uses the same pattern as ConnectPage. Records, transcribes
  // through /api/voice/transcribe, then appends to the intent.
  const startRecord = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        try {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          const fd = new FormData();
          fd.append("audio", blob, "intent.webm");
          const r = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          const t = (r.data?.transcript || "").trim();
          if (t) setIntent((cur) => (cur ? cur + " " + t : t));
          else toast("Didn't catch that — try again.");
        } catch {
          toast.error("Voice input failed. You can type instead.");
        }
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      track("personal_prayer_voice_input_started", {});
    } catch {
      toast.error("Microphone unavailable.");
    }
  };
  const stopRecord = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
    setRecording(false);
  };

  const charCount = intent.length;

  return (
    <div className="px-5 md:px-8 py-4 max-w-2xl mx-auto" data-testid="personal-prayer-start">
      <Link to="/guided-prayer" className="inline-flex items-center gap-1 text-[#8B7355] text-xs font-body mb-4">
        <ArrowLeft size={14} /> All journeys
      </Link>

      {/* Generating overlay — full-card replacement for clarity */}
      {generating && <GeneratingPanel />}

      {!generating && (
        <>
          <div className="text-center mb-6">
            <p className="text-[#C4A265] text-[10px] uppercase tracking-[0.25em] font-body mb-2 inline-flex items-center gap-1.5">
              <Sparkle size={11} weight="fill" /> Personalized Guided Prayer
            </p>
            <h1 className="font-heading text-2xl sm:text-3xl text-[#2D1B0E] font-medium leading-snug mb-2">
              {PROMPTS[promptIdx.current]}
            </h1>
            <p className="font-heading text-sm sm:text-base text-[#8B7355] italic">
              Voice will craft a Scripture-grounded prayer journey for you.
            </p>
          </div>

          {/* Input card */}
          <div className="glass-card-strong p-5 mb-5 relative">
            <textarea
              value={intent}
              onChange={(e) => setIntent(e.target.value)}
              maxLength={1500}
              rows={5}
              data-testid="intent-input"
              placeholder="A diagnosis, a decision, a fear, a doubt, a thanksgiving — name it in your own words."
              className="w-full bg-transparent font-body text-base text-[#2D1B0E] leading-relaxed focus:outline-none resize-none placeholder:text-[#B8A088]"
            />
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#E8DFD3]">
              <button
                onClick={recording ? stopRecord : startRecord}
                data-testid="voice-input-btn"
                className={`inline-flex items-center gap-1.5 text-xs font-body ${recording ? "text-rose-700" : "text-[#8B7355] hover:text-[#5C4033]"}`}
              >
                {recording ? <><StopCircle size={14} weight="fill" /> Stop</> : <><Microphone size={14} /> Speak it</>}
              </button>
              <span className="text-[10px] text-[#B8A088] font-body">{charCount}/1500</span>
            </div>
          </div>

          {/* Shortcuts */}
          <p className="text-[#8B7355] text-[10px] uppercase tracking-wider font-body mb-2">Or start with</p>
          <div className="grid grid-cols-2 gap-2 mb-6" data-testid="shortcut-grid">
            {SHORTCUTS.map((s) => {
              const active = shortcut === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => pickShortcut(s.id)}
                  data-testid={`shortcut-${s.id}`}
                  className={`group flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-all ${
                    active ? "border-[#C4A265] bg-[#FAF5EC]" : "border-[#E8DFD3] bg-white/60 hover:bg-white/80"
                  }`}
                >
                  <s.icon size={16} weight="duotone" className="text-[#C4A265] flex-shrink-0" />
                  <span className="font-body text-sm text-[#2D1B0E]">{s.label}</span>
                </button>
              );
            })}
          </div>

          {/* Pray-for-me-too share opt-in */}
          <div
            className={`mb-5 rounded-xl border transition-all ${
              shareOptIn ? "border-[#C4A265] bg-[#FAF5EC]" : "border-[#E8DFD3] bg-white/60"
            }`}
            data-testid="share-card"
          >
            <div className="p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={shareOptIn}
                  onChange={(e) => {
                    if (e.target.checked) {
                      // Don't flip to true silently — preview first.
                      loadPreview();
                    } else {
                      setShareOptIn(false);
                      setPreviewData(null);
                    }
                  }}
                  data-testid="share-checkbox"
                  className="mt-0.5 accent-[#C4A265] w-4 h-4 flex-shrink-0"
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <UsersThree size={14} weight="duotone" className="text-[#C4A265]" />
                    <span className="font-body text-sm font-medium text-[#2D1B0E]">Pray for me too</span>
                    {shareOptIn && (
                      <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-medium">on</span>
                    )}
                  </div>
                  <p className="font-body text-xs text-[#5C4033] leading-relaxed">
                    Share an <b>anonymous</b> version of this need with a matching prayer circle so others can pray with you.
                  </p>
                  <p className="font-body text-[11px] text-[#8B7355] italic mt-1">
                    Names, locations, and personal details are stripped. You'll see the exact wording before it's shared.
                  </p>
                  {shareOptIn && previewData && (
                    <button
                      type="button"
                      onClick={() => setShowPreview(true)}
                      data-testid="re-preview-btn"
                      className="mt-2 text-[11px] text-[#C4A265] hover:text-[#5C4033] underline-offset-2 hover:underline font-body"
                    >
                      Review the wording
                    </button>
                  )}
                  {previewLoading && (
                    <p className="text-[11px] text-[#8B7355] italic mt-2 inline-flex items-center gap-1">
                      <Sparkle size={11} weight="fill" className="text-[#C4A265] animate-pulse" /> Preparing a safe preview…
                    </p>
                  )}
                </div>
              </label>
            </div>
          </div>

          {/* Generate */}
          <button
            onClick={generate}
            disabled={intent.trim().length < 4}
            data-testid="generate-btn"
            className="w-full bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white rounded-full py-4 font-body text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
          >
            <HandsPraying size={14} weight="duotone" />
            Begin My Prayer Journey
            <ArrowRight size={14} />
          </button>

          <p className="text-[#B8A088] text-[10px] font-body italic text-center mt-4 leading-relaxed max-w-md mx-auto">
            Every prayer journey is anchored in Scripture. Voice does not diagnose, prescribe, or replace
            a pastor — it walks beside you, prayerfully.
          </p>
        </>
      )}

      {/* Preview modal */}
      {showPreview && previewData && (
        <SharePreviewModal
          data={previewData}
          onConfirm={confirmPreview}
          onCancel={cancelPreview}
          onEdit={(s) => setPreviewData({ ...previewData, summary: s })}
        />
      )}
    </div>
  );
}

function SharePreviewModal({ data, onConfirm, onCancel, onEdit }) {
  const [edited, setEdited] = useState(data.summary);
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[#2D1B0E]/40 backdrop-blur-sm p-4"
      onClick={onCancel}
      data-testid="share-preview-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-[#FFFDF8] rounded-2xl border border-[#E8DFD3] shadow-2xl overflow-hidden"
      >
        <div className="p-5 border-b border-[#E8DFD3] flex items-start justify-between gap-3">
          <div>
            <p className="text-[#C4A265] text-[10px] uppercase tracking-[0.25em] font-body font-medium mb-1 inline-flex items-center gap-1.5">
              <ShieldCheck size={11} weight="fill" /> Safe to share
            </p>
            <h3 className="font-heading text-lg text-[#2D1B0E] font-medium">Review what others will see.</h3>
          </div>
          <button onClick={onCancel} aria-label="Close" className="text-[#8B7355] hover:text-[#5C4033]" data-testid="preview-close-btn">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <p className="text-[#8B7355] text-[10px] uppercase tracking-wider font-body mb-1.5">The anonymous prayer message</p>
            <textarea
              value={edited}
              onChange={(e) => setEdited(e.target.value)}
              data-testid="preview-summary"
              maxLength={300}
              rows={3}
              className="w-full px-3 py-2 rounded-md border border-[#E8DFD3] bg-white font-body text-sm text-[#2D1B0E] leading-relaxed focus:outline-none focus:border-[#C4A265]"
            />
            <p className="text-[10px] text-[#B8A088] font-body italic mt-1">
              You can soften the wording. Don't add names or specifics — they'll be stripped again.
            </p>
          </div>

          {data.suggested_circle && (
            <div className="rounded-md bg-[#FAF5EC] border border-[#E8DFD3] p-3" data-testid="preview-circle">
              <p className="text-[#8B7355] text-[10px] uppercase tracking-wider font-body mb-0.5">Going to</p>
              <p className="font-body text-sm font-medium text-[#2D1B0E] inline-flex items-center gap-1.5">
                <UsersThree size={14} weight="duotone" className="text-[#C4A265]" />
                {data.suggested_circle.name}
                <span className="text-[10px] text-[#8B7355]">· {data.suggested_circle.topic}</span>
              </p>
            </div>
          )}

          {data.redactions?.length > 0 && (
            <div className="text-[11px] text-[#8B7355] font-body" data-testid="preview-redactions">
              <span className="font-medium">Stripped before sharing:</span>{" "}
              {data.redactions.join(" · ")}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-[#E8DFD3] flex gap-2">
          <button
            onClick={onCancel}
            data-testid="preview-cancel-btn"
            className="flex-1 px-4 py-2 rounded-full glass-card text-[#5C4033] text-xs font-body hover:bg-white/60"
          >
            Cancel
          </button>
          <button
            onClick={() => { onEdit(edited); onConfirm(); }}
            data-testid="preview-confirm-btn"
            className="flex-1 px-4 py-2 rounded-full bg-[#2D1B0E] text-white text-xs font-body font-medium hover:bg-[#3D2B1F] inline-flex items-center justify-center gap-1.5"
          >
            <ShieldCheck size={12} weight="fill" /> Share with circle
          </button>
        </div>
      </div>
    </div>
  );
}

const REASSURANCES = [
  "Listening to what you're carrying…",
  "Searching the Scriptures for you…",
  "Shaping each step with care…",
  "Anchoring your prayer in God's Word…",
  "Almost there — preparing a quiet space…",
];

function GeneratingPanel() {
  const [phraseIdx, setPhraseIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPhraseIdx((i) => (i + 1) % REASSURANCES.length), 2400);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="glass-card-strong p-8 sm:p-10 text-center relative overflow-hidden" data-testid="generating-panel">
      <div className="spiritual-orb-sm w-56 h-56 -top-16 -right-16 animate-orb opacity-60" />
      <div className="spiritual-orb-sm w-44 h-44 -bottom-14 -left-12 animate-orb opacity-40" />
      <div className="relative z-10">
        <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-gradient-to-br from-[#FAF5EC] via-[#EDE6DC] to-[#E5D9C7] flex items-center justify-center shadow-inner">
          <Sparkle size={26} weight="fill" className="text-[#C4A265] animate-pulse" />
        </div>
        <p className="text-[#C4A265] text-[10px] uppercase tracking-[0.3em] font-body font-medium mb-3">
          Crafting your journey
        </p>
        <h2 className="font-heading text-xl sm:text-2xl text-[#2D1B0E] font-medium mb-2 leading-snug">
          {REASSURANCES[phraseIdx]}
        </h2>
        <p className="text-[#8B7355] font-body text-xs italic mb-6">
          This usually takes 10–20 seconds.
        </p>
        <div className="mx-auto w-40 h-1 rounded-full bg-[#E8DFD3] overflow-hidden">
          <div className="h-full bg-[#C4A265] animate-pulse" style={{ width: "65%" }} />
        </div>
        <p className="text-[#B8A088] font-body text-[11px] italic mt-6 leading-relaxed max-w-sm mx-auto">
          "Be still, and know that I am God." — Psalm 46:10
        </p>
      </div>
    </div>
  );
}
